import { JSX, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import { MultiSelect } from 'components/MultiSelect';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useFetchEquipmentTypes } from 'hooks/useFetchEquipmentTypes';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { validateRequiredSelectMembership } from 'utils/formValidation';
import { normalizeToArray } from 'utils/loadInfoAndBuilding';

import {
  PLS_LOAD_SIZE_OPTIONS,
  PLS_SECTION_LABELS,
  PLS_UNCONFIRMED_EQUIPMENT_HINT,
} from './constants';
import {
  PlsEquipmentOption,
  buildPlsEquipmentOptions,
  plsEquipmentClassLabel,
  plsEquipmentMatchesSearch,
  plsUnlistedEquipmentOption,
  resolvePlsEquipmentCode,
} from './equipmentOptions';
import { usePlsAccessorials } from './hooks/usePlsReferenceCatalogs';
import { PlsFormAccessorialItem, PlsFormValues } from './types';
import {
  plsAccessorialCodeFromValue,
  plsAccessorialOptions,
  toSelectStringValue,
} from './utils';

type SpecificationsFormProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  tmsID?: number;
  /** Lock after create; PLS rejects loadSize changes on update. */
  isCreateMode?: boolean;
};

function resolveAccessorialCode(
  item: PlsFormAccessorialItem,
  catalog: { id: number; code?: string; name?: string }[]
): string {
  const itemCode = String(item?.code ?? '').trim();
  if (itemCode) {
    return itemCode.toUpperCase();
  }
  const valueCode = plsAccessorialCodeFromValue(item?.value);
  if (valueCode) {
    return valueCode.toUpperCase();
  }
  if (item?.id != null) {
    const match = catalog.find((accessorial) => accessorial?.id === item.id);
    const matchCode = String(match?.code ?? '').trim();
    if (matchCode) {
      return matchCode.toUpperCase();
    }
  }
  if (item?.displayText) {
    const itemText = item.displayText.trim().toLowerCase();
    const match = catalog.find(
      (accessorial) =>
        String(accessorial?.name ?? '')
          .trim()
          .toLowerCase() === itemText
    );
    const matchCode = String(match?.code ?? '').trim();
    if (matchCode) {
      return matchCode.toUpperCase();
    }
  }
  return '';
}

function accessorialSelectionKey(
  item: PlsFormAccessorialItem,
  catalog: { id: number; code?: string; name?: string }[]
): string {
  const resolvedCode = resolveAccessorialCode(item, catalog);
  return (
    resolvedCode ||
    String(item?.code ?? '').trim() ||
    (item?.id != null ? String(item.id) : '')
  ).toUpperCase();
}

function PlsEquipmentOptionRow({
  option,
}: {
  option: PlsEquipmentOption;
}): JSX.Element {
  const classLabel = plsEquipmentClassLabel(option.transportType);
  return (
    <Flex align='center' gap='sm'>
      <span>{option.label}</span>
      {classLabel && (
        <Typography variant='body-xs' className='shrink-0 text-neutral-500'>
          {classLabel}
        </Typography>
      )}
    </Flex>
  );
}

export function SpecificationsForm({
  formMethods,
  tmsID,
  isCreateMode = true,
}: SpecificationsFormProps): JSX.Element {
  const {
    control,
    formState: { defaultValues, errors },
    getValues,
    setValue,
    trigger,
  } = formMethods;

  const { equipmentTypes, isLoading: isLoadingEquipment } =
    useFetchEquipmentTypes(tmsID ?? 0);

  const { accessorials, isLoading: isLoadingAccessorials } =
    usePlsAccessorials(tmsID);

  const equipmentOptions = useMemo(
    () => buildPlsEquipmentOptions(equipmentTypes),
    [equipmentTypes]
  );
  const transportType = formMethods.watch('specifications.transportType');
  // The AI-filled hint itself disappears once the rep changes the field.
  const isEquipmentUnconfirmed =
    isCreateMode &&
    Boolean(defaultValues?.specifications?.transportTypeUnconfirmed);

  useEffect(() => {
    const current = transportType?.trim() ?? '';
    const resolved = resolvePlsEquipmentCode(current, equipmentOptions);
    if (resolved && resolved !== current) {
      // Safe in create and edit: map a catalog name or code casing to the exact code.
      setValue('specifications.transportType', resolved, {
        shouldValidate: isCreateMode,
      });
    }
  }, [equipmentOptions, isCreateMode, setValue, transportType]);

  useEffect(() => {
    if (
      !isCreateMode ||
      isLoadingEquipment ||
      (equipmentTypes ?? []).length === 0
    ) {
      return;
    }
    const current = String(
      getValues('specifications.transportType') ?? ''
    ).trim();
    if (!current) {
      return;
    }
    void trigger('specifications.transportType');
  }, [equipmentTypes, getValues, isCreateMode, isLoadingEquipment, trigger]);

  const watchedAccessorials = useWatch({
    control,
    name: 'accessorials',
    defaultValue: [],
  });

  const accessorialOptions = useMemo(() => {
    const catalog = Array.isArray(accessorials) ? accessorials : [];
    const options = plsAccessorialOptions(catalog);

    const current = normalizeToArray(watchedAccessorials) ?? [];
    for (const item of current) {
      const code = accessorialSelectionKey(item, catalog);

      if (code && !options.some((opt) => opt.value === code)) {
        const name = String(item?.displayText ?? '').trim();
        const label =
          name && name.toUpperCase() !== code
            ? `${name} (${code})`
            : name || code;
        options.push({ value: code, label });
      }
    }

    return options;
  }, [accessorials, watchedAccessorials]);

  useEffect(() => {
    if (!Array.isArray(accessorials) || accessorials.length === 0) {
      return;
    }
    const current =
      normalizeToArray(formMethods.getValues('accessorials')) ?? [];
    if (current.length === 0) {
      return;
    }

    let needsSync = false;
    const synced = current.map((item) => {
      if (item && item.code && item.code.trim()) {
        return item;
      }
      const resolvedCode = resolveAccessorialCode(item, accessorials);
      if (!resolvedCode) {
        return item;
      }
      needsSync = true;
      const matched = accessorials.find(
        (a) =>
          String(a?.code ?? '')
            .trim()
            .toUpperCase() === resolvedCode
      );
      return {
        ...item,
        code: resolvedCode,
        displayText: item.displayText || matched?.name || resolvedCode,
        id: item.id || matched?.id || 0,
        value: item.value ?? matched?.defaultRate ?? 0,
      };
    });

    if (needsSync) {
      setValue('accessorials', synced as any, { shouldDirty: false });
    }
  }, [accessorials, formMethods, setValue]);

  const selectedAccessorialCodes = useMemo(() => {
    const catalog = Array.isArray(accessorials) ? accessorials : [];
    return (normalizeToArray(watchedAccessorials) ?? [])
      .map((item) => accessorialSelectionKey(item, catalog))
      .filter(Boolean);
  }, [watchedAccessorials, accessorials]);

  const selectedAccessorialDetails = useMemo(() => {
    const catalog = Array.isArray(accessorials) ? accessorials : [];
    const items = normalizeToArray(watchedAccessorials) ?? [];

    return items
      .map((item, index) => {
        const resolvedCode = resolveAccessorialCode(item, catalog);
        const matched = catalog.find(
          (a) =>
            (resolvedCode &&
              String(a?.code ?? '')
                .trim()
                .toUpperCase() === resolvedCode) ||
            (item?.id != null && a?.id === item.id)
        );

        const code = (
          resolvedCode ||
          String(item?.code ?? '').trim() ||
          String(matched?.code ?? '').trim()
        ).toUpperCase();

        const name = String(
          matched?.name || item?.displayText || code || ''
        ).trim();

        const label =
          name && code && name.trim() !== code
            ? `${name} (${code})`
            : name || code || `Item ${index + 1}`;

        return {
          key: code || item?.id || index,
          code,
          label,
        };
      })
      .filter((item) => Boolean(item.label));
  }, [accessorials, watchedAccessorials]);

  const accessorialSummaryText = useMemo(() => {
    if (selectedAccessorialDetails.length === 0) {
      return isLoadingAccessorials ? 'Loading accessorials...' : 'None';
    }
    if (selectedAccessorialDetails.length <= 2) {
      return selectedAccessorialDetails.map((a) => a.label).join(', ');
    }
    const firstTwo = selectedAccessorialDetails
      .slice(0, 2)
      .map((a) => a.label)
      .join(', ');
    const remaining = selectedAccessorialDetails.length - 2;
    return `${firstTwo}, +${remaining} more`;
  }, [isLoadingAccessorials, selectedAccessorialDetails]);

  const viewModeAccessorialOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];
    selectedAccessorialDetails.forEach((item, index) => {
      const rawVal = String(item.code || item.key || index);
      const value = seen.has(rawVal) ? `${rawVal}-${index}` : rawVal;
      seen.add(value);
      options.push({ value, label: item.label });
    });
    return options;
  }, [selectedAccessorialDetails]);

  const handleAccessorialsChange = (newCodes: string[]) => {
    const catalog = Array.isArray(accessorials) ? accessorials : [];
    const current = normalizeToArray(getValues('accessorials')) ?? [];
    const nextAccessorials = newCodes
      .map((code) => {
        const target = code.trim().toUpperCase();
        const matched = catalog.find(
          (accessorial) =>
            String(accessorial?.code ?? '')
              .trim()
              .toUpperCase() === target
        );
        if (matched?.code) {
          return {
            id: matched.id,
            displayText: matched.name || matched.code,
            code: matched.code,
            value: matched.defaultRate ?? 0,
          } satisfies PlsFormAccessorialItem;
        }

        return (
          current.find(
            (item) => accessorialSelectionKey(item, catalog) === target
          ) ?? null
        );
      })
      .filter(Boolean) as PlsFormAccessorialItem[];

    setValue('accessorials', nextAccessorials, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  return (
    <Grid cols='2' gap='sm' className='w-full m-0'>
      <div className='col-span-2'>
        <Label name='mode' required>
          {PLS_SECTION_LABELS.loadSize}
        </Label>
        <Controller
          name='mode'
          control={control}
          rules={{ required: 'Required' }}
          render={({ field }) => (
            <Select
              name={field.name}
              className='h-8'
              placeholder='Select load size'
              disabled={!isCreateMode}
              onChange={(value) => {
                const loadSize = toSelectStringValue(value);
                field.onChange(loadSize);
                setValue('specifications.transportSize', loadSize, {
                  shouldDirty: true,
                });
              }}
              value={field.value || undefined}
              options={PLS_LOAD_SIZE_OPTIONS}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name='mode'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-2'>
        <div className='flex items-center gap-2'>
          <Label
            name='specifications.transportType'
            required
            aiHintTooltip={
              isEquipmentUnconfirmed
                ? PLS_UNCONFIRMED_EQUIPMENT_HINT
                : undefined
            }
          >
            {PLS_SECTION_LABELS.equipment}
          </Label>
          {isLoadingEquipment && <ButtonLoader className='h-3 w-3' />}
        </div>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={{
            validate: (value) => {
              if (!isCreateMode) {
                return validateEquipmentRequired(value);
              }
              return validateRequiredSelectMembership(
                value,
                equipmentOptions.map((option) => option.value),
                {
                  isLoading: isLoadingEquipment,
                  message: EQUIPMENT_REQUIRED_MESSAGE,
                }
              );
            },
          }}
          render={({ field }) => {
            const selected = String(field.value ?? '').trim();
            const inOptions = equipmentOptions.some(
              (option) => option.value === selected
            );
            // Load View shows the code PLS has even when the catalog no longer lists it.
            const showUnlisted =
              !isCreateMode &&
              !isLoadingEquipment &&
              Boolean(selected) &&
              !inOptions;
            const options = showUnlisted
              ? [...equipmentOptions, plsUnlistedEquipmentOption(selected)]
              : equipmentOptions;
            return (
              <Select
                showSearch
                name={field.name}
                className='h-8'
                placeholder={
                  isLoadingEquipment
                    ? 'Loading equipment...'
                    : EQUIPMENT_PLACEHOLDER
                }
                disabled={isLoadingEquipment}
                filterOption={plsEquipmentMatchesSearch}
                optionRender={(option) => (
                  <PlsEquipmentOptionRow
                    option={option as PlsEquipmentOption}
                  />
                )}
                onChange={(value) => {
                  const next = toSelectStringValue(value);
                  // The select reports empty when the catalog arrives after a saved
                  // code. That code is still the one on the load.
                  if (!next && selected && (inOptions || showUnlisted)) {
                    return;
                  }
                  field.onChange(next);
                }}
                value={
                  selected && (inOptions || showUnlisted) ? selected : undefined
                }
                options={options}
                data-testid='pls-equipment-select'
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name='specifications.transportType'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-2'>
        <div className='flex items-center gap-2'>
          <Label name='accessorials'>{PLS_SECTION_LABELS.accessorials}</Label>
          {isLoadingAccessorials && <ButtonLoader className='h-3 w-3' />}
        </div>
        {isCreateMode ? (
          <MultiSelect
            placeholder={
              isLoadingAccessorials
                ? 'Loading accessorials...'
                : 'Select accessorials...'
            }
            value={selectedAccessorialCodes}
            onChange={handleAccessorialsChange}
            options={accessorialOptions}
            disabled={isLoadingAccessorials}
            className='w-full'
            displayMode='count'
          />
        ) : (
          <div
            className='w-full'
            title={
              selectedAccessorialDetails.length > 0
                ? selectedAccessorialDetails
                    .map((item) => item.label)
                    .join(', ')
                : undefined
            }
          >
            <Select
              showSearch
              className='h-8'
              placeholder={
                isLoadingAccessorials ? 'Loading accessorials...' : 'None'
              }
              disabled={selectedAccessorialDetails.length === 0}
              labelInValue
              value={
                selectedAccessorialDetails.length > 0
                  ? { label: accessorialSummaryText, value: '__selected__' }
                  : undefined
              }
              options={viewModeAccessorialOptions}
              onChange={() => {}}
              data-testid='pls-readonly-accessorials-trigger'
            />
          </div>
        )}
      </div>
    </Grid>
  );
}
